/**
 * /onboarding — bare token-resolution route.
 *
 * Resolves the signed-in subscriber's onboarding token server-side and
 * forwards them to the canonical tokenized flow at `/onboarding/$token`. This
 * removes the Phase 1 stopgap (portal-guard.ts) that signed a valid
 * mid-onboarding subscriber out and forced a marketing round trip + magic-link
 * re-entry just to recover their token.
 *
 * Auth-gated (not token-gated). The token lives in `onboarding_state` and is
 * readable under RLS only by the row's owner (`onboarding_state_subscriber_
 * select`, migration 0007), so an authenticated subscriber lands here, reads
 * their own token, and is redirected to `/onboarding/<token>`.
 *
 * Outcomes:
 *   - No session                 -> /auth/sign-in (with redirect back here).
 *   - Onboarding complete         -> /portal (nothing left to do; the portal
 *     (eric_reviewed)               entry guard takes over from there).
 *   - Token resolves              -> /onboarding/<token> (the tokenized flow
 *                                    enforces its own status/active checks).
 *   - No onboarding row / no      -> /auth/sign-out, matching the conservative
 *     token (operator, stale         portal-guard fallback. An operator has no
 *     session, read error)           onboarding row; a stale session that maps
 *                                    to no row should not loop here.
 *
 * This route only READS `onboarding_state` (Phase F owns writes). It does not
 * import or modify any Phase F file.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { getCurrentSession } from '@/lib/auth';
import { fetchOwnOnboardingResume } from '@/lib/portal';

export const Route = createFileRoute('/onboarding/')({
  loader: async () => {
    const session = await getCurrentSession();
    if (!session) {
      throw redirect({
        to: '/auth/sign-in',
        search: { redirect: '/onboarding' },
      });
    }

    const resume = await fetchOwnOnboardingResume();
    if (!resume) {
      // No onboarding row for this session (operator, or a stale session that
      // maps to no subscriber). Conservative fallback, same as portal-guard.
      throw redirect({ to: '/auth/sign-out' });
    }

    if (resume.onboarding_status === 'eric_reviewed') {
      // Onboarding is finished; the portal is the right home.
      throw redirect({ to: '/portal' });
    }

    throw redirect({
      to: '/onboarding/$token',
      params: { token: resume.onboarding_token },
    });
  },
});
